import { PreScreeningQuestion, QuestionType } from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import { CARDIOVASCULAR_ATTRIBUTES } from "../../utils/attributeMapping/attributeConstants";

/**
 * Alnylam Hypertension Study — pre-screener.
 *
 * Lightweight qualifying questions only. The most valuable screening signal
 * (recent home BP readings) is collected on the thank-you page via the
 * BP-image upload flow rather than asking users to self-report numbers in
 * the questionnaire.
 */
export const alnylamHypertensionScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      { value: "18_or_older", label: "I am 18 or older" },
      { value: "under_18", label: "I am under 18", disqualify: true },
    ],
    disqualifyMessage:
      "This study is only for adults aged 18 and over.",
    attributeMapping: mappers.value({
      "18_or_older": [],
      under_18: [],
    }),
  },

  {
    id: "hypertension_diagnosis",
    type: QuestionType.RADIO,
    question:
      "Have you been diagnosed with high blood pressure (hypertension) by a doctor?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_diagnosed",
        label: "Yes, I have been diagnosed with high blood pressure",
      },
      {
        value: "no_diagnosis",
        label: "No, I have not been diagnosed with high blood pressure",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is for adults who have been diagnosed with high blood pressure.",
    attributeMapping: mappers.value({
      yes_diagnosed: [
        {
          attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION,
          booleanAttributeValue: true,
        },
      ],
      no_diagnosis: [
        {
          attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION,
          booleanAttributeValue: false,
        },
      ],
    }),
  },

  {
    id: "bp_treatment_status",
    type: QuestionType.RADIO,
    question: "Are you currently taking medication for your blood pressure?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "currently_treated",
        label: "Yes, I currently take blood pressure medication",
      },
      {
        value: "not_currently_treated",
        label: "No, I am not currently taking blood pressure medication",
      },
    ],
    attributeMapping: mappers.value({
      currently_treated: [],
      not_currently_treated: [],
    }),
  },

  {
    id: "recent_bp_reading",
    type: QuestionType.RADIO,
    question:
      "Do you have access to recent blood pressure readings (from a home monitor, doctor, or health app)?",
    helpText:
      "We'll ask you to share a few readings on the next screen so the study team can confirm whether you're a fit.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_have_readings",
        label: "Yes, I can share recent readings",
      },
      {
        value: "can_take_new",
        label: "No, but I can take new readings over the next few days",
      },
    ],
    attributeMapping: mappers.value({
      yes_have_readings: [],
      can_take_new: [],
    }),
  },

  {
    id: "cardiovascular_event_recent",
    type: QuestionType.RADIO,
    question:
      "In the past 6 months, have you had a heart attack, stroke, or been hospitalized for a heart condition?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no_recent_event",
        label: "No, I have not had any of these in the past 6 months",
      },
      {
        value: "yes_recent_event",
        label:
          "Yes, I have had a heart attack, stroke, or heart-related hospitalization in the past 6 months",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study cannot include people with a recent serious cardiovascular event. Please speak with your doctor about other options.",
    attributeMapping: mappers.value({
      no_recent_event: [],
      yes_recent_event: [],
    }),
  },

  {
    id: "pregnancy_status",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant, breastfeeding, or planning to become pregnant in the next year?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no_pregnancy",
        label: "No, none of these apply to me",
      },
      {
        value: "yes_pregnancy",
        label: "Yes, one or more of these apply to me",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study cannot include people who are pregnant, breastfeeding, or planning to become pregnant in the near future.",
    attributeMapping: mappers.value({
      no_pregnancy: [],
      yes_pregnancy: [],
    }),
  },
];
